import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { getDeviceResetTime } from 'src/@api/login'
import dayjs from 'dayjs'
import { disconnectCheck, getAllDashboard } from 'src/@api/dashboard'
import { readInitialDeviceUserId } from 'src/utils/resolveUpgradeDeviceId'
import { readStoredDeviceContext } from 'src/utils/deviceContext'

const initialDeviceContext = readStoredDeviceContext()

// 将 USER_SUMMARY 的下载完成时间（毫秒时间戳，0 表示未完成）格式化为展示字符串
const formatDownloadCompleteTime = value => {
  if (value == null || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return ''

  // 正常为毫秒；小于 1e12 时按秒级兼容
  const ms = n < 1e12 ? n * 1000 : n
  const formatted = dayjs(ms).format('YYYY-MM-DD HH:mm:ss')

  return formatted === 'Invalid Date' ? '' : formatted
}

/**
 * 拉取设备数据更新时间。
 * iOS iCloud：USER_SUMMARY；安卓 Local / 谷歌云：旧 reset-time 接口。
 * 携带 requestKey，fulfilled 时丢弃切换前的响应。
 */
const formatDeviceDownloadTime = value =>
  value != null && value !== '' ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : ''

const fetchResetTimeRequest = createAsyncThunk('common/fetchResetTime', async ({ username }) => {
  const mode =
    typeof window !== 'undefined' ? localStorage.getItem('currentMode') || 'iOS-Cloud' : 'iOS-Cloud'
  const resolvedUsername = username || ''

  // iOS iCloud：
  // - USER_SUMMARY.*_download_complete_time → Updated 展示（下载完成才有）
  // - user-device-reset-time.advanced_download_time → 是否完成高级绑定（开始下载就有）
  if (mode === 'iOS-Cloud') {
    const empty = {
      basicUpdateDownloadTime: '',
      advancedUpdateDownloadTime: '',
      advancedDownloadTime: '',
      username: resolvedUsername
    }

    if (!resolvedUsername) return empty

    try {
      const [summaryRes, resetRes] = await Promise.all([
        getAllDashboard({
          username: resolvedUsername,
          params: {
            category: 'USER_SUMMARY'
          }
        }),
        getDeviceResetTime({ username: resolvedUsername }).catch(() => null)
      ])
      const data = summaryRes?.data || {}
      const resetData = resetRes?.data || {}

      return {
        basicUpdateDownloadTime: formatDownloadCompleteTime(data.basic_download_complete_time), // 一步/基础完成
        advancedUpdateDownloadTime: formatDownloadCompleteTime(data.advance_download_complete_time), // 二步/高级完成
        // 高级验证触发时间（send-download-time 写入，非下载完成）
        advancedDownloadTime: formatDeviceDownloadTime(resetData.advanced_download_time),
        username: resolvedUsername
      }
    } catch (e) {
      return empty
    }
  }

  // 安卓 Local / 谷歌云：仍用 /definition/user-device-reset-time（触发时间即展示时间）
  let res = await getDeviceResetTime({ username: resolvedUsername })
  const basicTime = formatDeviceDownloadTime(res.data?.basic_download_time)
  const advancedTime = formatDeviceDownloadTime(res.data?.advanced_download_time)

  return {
    basicUpdateDownloadTime: basicTime,
    advancedUpdateDownloadTime: advancedTime,
    advancedDownloadTime: advancedTime,
    username: resolvedUsername
  }
})

export const fetchResetTime = () => (dispatch, getState) => {
  const state = getState()
  const username = JSON.parse(localStorage.getItem('DeviceUser') || '{}')?.username || ''

  // 携带当前 requestKey，fulfilled 时丢弃切换前的响应
  return dispatch(fetchResetTimeRequest({
    username,
    requestKey: state.Common.deviceContext?.requestKey || ''
  }))
}

export const advancedShow = createAsyncThunk('common/advancedShow', async () => {
  return true
})

export const fetchDeviceUser = createAsyncThunk('common/fetchDeviceUser', async row => {
  return row
})

export const setCurrentTab = createAsyncThunk('common/setCurrentTab', async value => {
  return value
})

const getConnectionStatusRequest = createAsyncThunk('common/getConnectionStatus', async ({ username }) => {
  const res = await disconnectCheck({
    deviceid: username
  })

  if (res.msg === 'disconnect') {
    return true
  }

  return false
})

export const getConnectionStatus = () => (dispatch, getState) => {
  const state = getState()
  const username = JSON.parse(localStorage.getItem('DeviceUser') || '{}')?.username || ''

  // 携带当前 requestKey，fulfilled 时丢弃切换前的响应
  return dispatch(getConnectionStatusRequest({
    username,
    requestKey: state.Common.deviceContext?.requestKey || ''
  }))
}

export const toggleNavVisibility = createAsyncThunk('common/toggleNavVisibility', async value => {
  return value
})

// 增值服务状态更改
export const changeExtremeService = createAsyncThunk('common/changeExtremeService', async value => {
  return value
})


// 正在下载提示信息是否弹窗状态更改
export const changeDownloadTips = createAsyncThunk('common/changeDownloadTips', async value => {
  return value
})

// 一步验证完成状态更改
export const changeBasicVerfiyOver = createAsyncThunk('common/changeBasicVerfiyOver', async value => {
  return value
})

export const common = createSlice({
  name: 'Common',
  initialState: {
    advancedShow: false,
    deviceUser: readInitialDeviceUserId(),
    currentMode:
      typeof window !== 'undefined' ? localStorage.getItem('currentMode') || '' : '',
    deviceSwitchRevision: 0,
    deviceContext: initialDeviceContext,
    activeResetTimeRequestId: '',
    activeConnectionRequestId: '',
    isDeviceSwitching: false,
    resetTime: {
      basicUpdateDownloadTime: null,
      advancedUpdateDownloadTime: null,

      // 高级绑定是否已触发（来自 user-device-reset-time.advanced_download_time）
      advancedDownloadTime: null
    },
    currentTab: '',
    isDisconnect: false,
    navVisible: false,
    extremeService: true, // 拥有增值服务
    downloadTips: false, // 是否弹窗正在下载提示信息
    basicVerfiyOver: false // 一步验证完成状态更改
  },
  reducers: {
    // 设备切换：更新选中 device_id；revision 递增以便同账号换物理机也能触发页面重拉
    deviceSwitched: (state, action) => {
      const payload = action.payload
      if (payload != null && typeof payload === 'object') {
        state.deviceUser = payload.deviceUserId ?? ''
        if (payload.currentMode != null && payload.currentMode !== '') {
          state.currentMode = payload.currentMode
        }
        if (payload.deviceContext) {
          state.deviceContext = payload.deviceContext
        }
      } else {
        state.deviceUser = payload ?? ''
      }
      state.deviceSwitchRevision += 1
      state.isDeviceSwitching = true
      state.isDisconnect = false

      // 切换中清空，避免沿用上一台设备的验证态导致闪屏/误判
      state.resetTime = {
        basicUpdateDownloadTime: null,
        advancedUpdateDownloadTime: null,
        advancedDownloadTime: null
      }
    },
    deviceSwitchFinished: state => {
      state.isDeviceSwitching = false
    },

    // 续费/升级成功：仅 bump revision，触发侧栏/遮罩重拉；不切设备、不进 switching 态
    subscriptionRefreshed: state => {
      state.deviceSwitchRevision += 1
    },
    setConnectionStatus: (state, action) => {
      state.isDisconnect = action.payload
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchResetTimeRequest.pending, (state, action) => {
      state.activeResetTimeRequestId = action.meta.requestId
    })
    builder.addCase(fetchResetTimeRequest.fulfilled, (state, action) => {
      if (
        state.deviceContext?.requestKey !== action.meta.arg.requestKey ||
        state.activeResetTimeRequestId !== action.meta.requestId
      ) return

      state.resetTime = action.payload

      // 不覆盖 deviceUser：选中键是 device_id，username 只用于接口
    })
    builder.addCase(advancedShow.fulfilled, (state, action) => {
      state.advancedShow = action.payload
    })
    builder.addCase(fetchDeviceUser.fulfilled, (state, action) => {
      state.deviceUser = action.payload
    })
    builder.addCase(setCurrentTab.fulfilled, (state, action) => {
      state.currentTab = action.payload
    })
    builder.addCase(getConnectionStatusRequest.pending, (state, action) => {
      state.activeConnectionRequestId = action.meta.requestId
    })
    builder.addCase(getConnectionStatusRequest.fulfilled, (state, action) => {
      if (
        state.deviceContext?.requestKey !== action.meta.arg.requestKey ||
        state.activeConnectionRequestId !== action.meta.requestId
      ) return

      state.isDisconnect = action.payload
    })
    builder.addCase(toggleNavVisibility.fulfilled, (state, action) => {
      state.navVisible = action.payload
    })
    builder.addCase(changeExtremeService.fulfilled, (state, action) => {
      state.extremeService = action.payload
    })
    builder.addCase(changeDownloadTips.fulfilled, (state, action) => {
      state.downloadTips = action.payload
    })
    builder.addCase(changeBasicVerfiyOver.fulfilled, (state, action) => {
      state.basicVerfiyOver = action.payload
    })
  }
})

export const { deviceSwitched, deviceSwitchFinished, subscriptionRefreshed, setConnectionStatus } = common.actions

/** @returns {Object} 当前 deviceContext（含 requestKey） */
export const selectDeviceContext = state => state.Common.deviceContext

/** @returns {string} 设备身份键 mode:account:physicalKey */
export const selectDeviceIdentityKey = state => state.Common.deviceContext?.identityKey || ''

/** @returns {string} 异步请求归属键，用于丢弃切换前的响应 */
export const selectDeviceRequestKey = state => state.Common.deviceContext?.requestKey || ''

export default common.reducer
